import { queryOptions, useQuery } from '@tanstack/react-query';
import type { DocsResult } from '@/services/auth/types';
import { COMMAND_DIALOG_QUERY_DEFAULTS } from './constants';

const TYPESENSE_HOST = 'https://d057q8ksiul4rg6vp-1.a1.typesense.net';
// nosemgrep: generic.secrets.security.detected-generic-api-key
// Public, read-only search key — same permissions as docs.customer.io client bundle.
// An env var wouldn't help: FE bundles embed the value at build time either way.
const TYPESENSE_SEARCH_KEY = 'wfHn4wdkFkMFhLBUrRoYnpCMojb5Ptop';

// Matches code-like headings (e.g. view_in_browser_url, message.id) that look
// broken as plain text in the preview panel. Typesense also returns duplicate
// sections for legacy/latest liquid versions — deduped by heading in select().
const CODE_HEADING_PATTERN = /^[a-z][\da-z]*([._][\da-z]+)+$/;

interface DocsSearchHit {
  alphaHeading: string;
  section: string;
  ref: string;
  headings: string[];
}

interface TypesenseGroupedHit {
  group_key: string[];
  hits: { document: DocsSearchHit }[];
}

interface TypesenseSearchResult {
  grouped_hits?: TypesenseGroupedHit[];
  found: number;
}

interface TypesenseMultiSearchResponse {
  results: TypesenseSearchResult[];
}

async function searchDocs(query: string, signal?: AbortSignal): Promise<TypesenseMultiSearchResponse> {
  const res = await fetch(`${TYPESENSE_HOST}/multi_search`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-TYPESENSE-API-KEY': TYPESENSE_SEARCH_KEY },
    signal,
    body: JSON.stringify({
      searches: [
        {
          collection: 'docs',
          q: query,
          query_by: 'alphaHeading,headings,content',
          sort_by: '_text_match:desc,weight:asc',
          group_by: 'ref',
          group_limit: 1,
          per_page: 10,
          include_fields: 'alphaHeading,section,ref,headings',
        },
      ],
    }),
  });

  if (!res.ok) {
    throw new Error(`Typesense search failed: ${res.status}`);
  }

  return res.json();
}

// Ported from docs repo: ui/src/components/SearchResults.tsx
function formatSection(section: string): string {
  if (section.toLowerCase() === 'sdk' || section.toLowerCase() === 'api') {
    return section.toUpperCase();
  }
  return section
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function mapDocsHitToSearchResult(hit: DocsSearchHit): DocsResult {
  return {
    kind: 'docs',
    doc_id: hit.ref,
    title: hit.alphaHeading,
    url: `https://docs.customer.io${hit.ref}`,
    other: { description: formatSection(hit.section) },
  };
}

export function docsSearchOptions(query: string) {
  return queryOptions({
    queryKey: ['docs-search', query],
    queryFn: ({ signal }) => searchDocs(query, signal),
    select: (data) => {
      const hits =
        data.results[0]?.grouped_hits
          ?.map((group) => group.hits[0]?.document)
          .filter((doc): doc is DocsSearchHit => doc != null) ?? [];
      return hits.map(mapDocsHitToSearchResult);
    },
    ...COMMAND_DIALOG_QUERY_DEFAULTS,
  });
}

interface DocsPageSection {
  alphaHeading: string;
  headings: string[];
  content: string;
}

interface TypesenseHit {
  document: DocsPageSection;
}

interface TypesenseSearchResultRaw {
  hits?: TypesenseHit[];
  found: number;
}

interface TypesenseMultiSearchRawResponse {
  results: TypesenseSearchResultRaw[];
}

async function fetchDocsSections(ref: string, signal?: AbortSignal): Promise<TypesenseMultiSearchRawResponse> {
  const res = await fetch(`${TYPESENSE_HOST}/multi_search`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-TYPESENSE-API-KEY': TYPESENSE_SEARCH_KEY },
    signal,
    body: JSON.stringify({
      searches: [
        {
          collection: 'docs',
          q: '*',
          filter_by: `ref:=\`${ref}\``,
          include_fields: 'alphaHeading,headings,content',
          per_page: 20,
        },
      ],
    }),
  });

  if (!res.ok) {
    throw new Error(`Typesense page fetch failed: ${res.status}`);
  }

  return res.json();
}

function docsPageOptions(ref: string) {
  return queryOptions({
    queryKey: ['docs-page', ref],
    queryFn: ({ signal }) => fetchDocsSections(ref, signal),
    // Typesense has no positional field, so we can't sort by DOM order.
    // Sort by heading depth as a heuristic — top-level sections (fewer
    // breadcrumb headings) appear first. For true page order, the docs
    // team would need to add a position field to the Typesense schema.
    select: (data) => {
      const sorted = (data.results[0]?.hits?.map((hit) => hit.document) ?? []).sort(
        (a, b) => a.headings.length - b.headings.length,
      );
      const seenHeadings = new Set<string>();
      return sorted.filter((section) => {
        const heading = section.headings[section.headings.length - 1] ?? section.alphaHeading;
        if (CODE_HEADING_PATTERN.test(heading) || seenHeadings.has(heading)) return false;
        seenHeadings.add(heading);
        return true;
      });
    },
    ...COMMAND_DIALOG_QUERY_DEFAULTS,
  });
}

export function useDocsPage(ref: string) {
  return useQuery({
    ...docsPageOptions(ref),
    enabled: ref.length > 0,
  });
}
